import { useState } from "react";

import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { Typography, Checkbox, Tabs, Tab } from "@portex-pro/ui-components";
import { Accessorial } from "api/types/generated-types";
import MemoizedTextInput from "app/pages/quote-request/_components/MemoizedTextInput";
import { useQuoteRequestSelector } from "app/pages/quote-request/_store";
import { quoteRequestActions } from "app/pages/quote-request/_store/quoteRequestSliceV2";
import { ShipmentAccessorial, ShipmentAccessorialType } from "app/pages/shipments/types/domain";
import AccesorialsIcon from "assets/quote-request/accesorials.svg";
import NotesIcon from "assets/quote-request/notes.svg";
import LocationPicker from "components/addresses/LocationPicker";
import DoubleTimeRangeSelector from "components/DoubleTimeRangeSelector";
import { Guide } from "components/Guide";
import SingleCalendarPicker from "components/SingleCalendarPicker";
import SingleTimeRangeSelector from "components/SingleTimeRangeSelector";
import { DateTime } from "luxon";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TimeRange } from "types/TimeRange";

type Props = {
  title: string;
  formKeyPrefix: string;
  accessorials?: Accessorial[] | null;
  defaultShowDateTime?: boolean;
};

const TabPanel = (props: { children?: React.ReactNode; value: number | null; index: number }) => {
  const { children, value, index } = props;
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <div>{children}</div>}
    </div>
  );
};

export const LocationStopSection: React.FC<Props> = ({
  title,
  accessorials,
  formKeyPrefix,
  defaultShowDateTime = false,
}) => {
  const { control, formState } = useFormContext();
  const { t } = useTranslation("quoteRequest");

  const [showDateTime, setShowDateTime] = useState(defaultShowDateTime);
  const [useTimeRange, setUseTimeRange] = useState(false);

  const [tabValue, setTabValue] = useState(null);

  const indexByPrefix = formKeyPrefix === "pickup" ? 0 : 1;

  const setQuoteRequestByPath = quoteRequestActions.useSetQuoteRequestByPath();
  const stops = useQuoteRequestSelector((store) => store.quoteRequestSliceV2.quoteRequest.stops);

  const getFieldError = (field: "address" | "date" | "time") => {
    const stopErrors = formState.errors?.[formKeyPrefix] as Record<string, unknown> | undefined;
    const error = stopErrors?.[field];
    if (!error) return undefined;
    if (typeof error === "object" && "message" in error) return error.message as string;
    return undefined;
  };

  const handleDateChange = (value: DateTime | null) => {
    const stop = stops?.[indexByPrefix];
    if (!value) {
      setQuoteRequestByPath({
        path: `stops.${indexByPrefix}`,
        value: { ...stops?.[indexByPrefix], start: null, end: null, is_time_tbd: false },
      });
      return;
    }
    let start = value
      ?.startOf("day")
      .setZone(stop?.address?.iana_timezone ?? DateTime.local().zone, { keepLocalTime: true });
    let end = value
      ?.endOf("day")
      .setZone(stop?.address?.iana_timezone ?? DateTime.local().zone, { keepLocalTime: true });

    if (start && stop?.start && stop.address?.iana_timezone) {
      start = start
        .setZone(stop.address.iana_timezone, { keepLocalTime: true })
        .set({ hour: DateTime.fromISO(stop.start).hour, minute: DateTime.fromISO(stop.start).minute });
    }
    if (end && stop?.end && stop.address?.iana_timezone) {
      end = end
        .setZone(stop.address.iana_timezone, { keepLocalTime: true })
        .set({ hour: DateTime.fromISO(stop.end).hour, minute: DateTime.fromISO(stop.end).minute });
    }
    setQuoteRequestByPath({
      path: `stops.${indexByPrefix}`,
      value: {
        ...stops?.[indexByPrefix],
        start: start.toISO(),
        end: end.toISO(),
        is_time_tbd: stops?.[indexByPrefix]?.is_time_tbd !== undefined ? undefined : true,
      },
    });
  };

  const handleTimeRangeChange = (range: TimeRange | null) => {
    const stop = stops?.[indexByPrefix];
    if (!range) {
      setQuoteRequestByPath({
        path: `stops.${indexByPrefix}`,
        value: { ...stops?.[indexByPrefix], start: null, end: null, is_time_tbd: false },
      });
      return;
    }
    let start: MaterialUiPickersDate | TimeRange | undefined = range.start;
    let end: MaterialUiPickersDate | TimeRange | undefined = range.end;

    if (stop?.address?.iana_timezone) {
      start = start?.setZone(stop?.address?.iana_timezone, { keepLocalTime: true });
      end = end?.setZone(stop?.address?.iana_timezone, { keepLocalTime: true });
    }
    setQuoteRequestByPath({
      path: `stops.${indexByPrefix}`,
      value: { ...stops?.[indexByPrefix], start: start?.toISO(), end: end?.toISO(), is_time_tbd: range.isTimeTBD },
    });
  };

  return (
    <div className="flex flex-col w-full mb-2">
      <div className="flex items-center w-full gap-3">
        <Controller
          control={control}
          name={`${formKeyPrefix}.address`}
          render={({ field, fieldState }) => (
            <Guide className="flex-1 min-w-0 flex flex-col z-20" showGuide={!field.value && !fieldState.error}>
              <LocationPicker
                mode="LTL"
                label={title}
                startIconPallete="grey"
                value={field.value}
                required={true}
                highlight={Boolean(getFieldError("address"))}
                onChange={(value) => {
                  setQuoteRequestByPath({ path: `stops.${indexByPrefix}.address`, value });
                }}
              />
              {getFieldError("address") && (
                <Typography color="error" variant="caption">
                  {getFieldError("address")}
                </Typography>
              )}
            </Guide>
          )}
        />
        <div className="flex items-center pt-6">
          <Checkbox
            style={{ padding: 0, marginRight: "8px" }}
            checked={showDateTime}
            onChange={(e) => setShowDateTime(e.target.checked)}
          />
          <Typography variant="body2">{t("LocationStep.addDateTime")}</Typography>
        </div>
        {showDateTime && (
          <>
            <div className="w-[200px] flex flex-col">
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {t("LocationStep.date")}
              </Typography>
              <Controller
                control={control}
                name={`${formKeyPrefix}`}
                render={({ field }) => (
                  <>
                    <SingleCalendarPicker
                      onChange={handleDateChange}
                      value={field.value.start ? DateTime.fromISO(field.value.start, { setZone: true }) : null}
                      startIconPallete="grey"
                    />
                  </>
                )}
              />
            </div>
            <div className="w-[200px] flex flex-col">
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {t("LocationStep.time")}
              </Typography>
              {useTimeRange ? (
                <Controller
                  control={control}
                  name={`${formKeyPrefix}`}
                  render={({ field }) => (
                    <>
                      <DoubleTimeRangeSelector
                        onChange={handleTimeRangeChange}
                        value={{
                          start: field.value.start ? DateTime.fromISO(field.value.start, { setZone: true }) : null,
                          end: field.value.end ? DateTime.fromISO(field.value.end, { setZone: true }) : null,
                          isTimeTBD: field.value.isTimeTBD,
                        }}
                        style={{ flex: 1 }}
                      />
                    </>
                  )}
                />
              ) : (
                <Controller
                  control={control}
                  name={`${formKeyPrefix}`}
                  render={({ field }) => (
                    <>
                      <SingleTimeRangeSelector
                        onChange={handleTimeRangeChange}
                        value={{
                          start: field.value.start ? DateTime.fromISO(field.value.start, { setZone: true }) : null,
                          end: field.value.end ? DateTime.fromISO(field.value.end, { setZone: true }) : null,
                          isTimeTBD: field.value.isTimeTBD,
                        }}
                        style={{ flex: 1 }}
                        startIconPallete="grey"
                      />
                    </>
                  )}
                />
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex justify-between w-full -mt-2 items-center">
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => (newValue !== tabValue ? setTabValue(newValue) : setTabValue(null))}
          textColor="primary"
          TabIndicatorProps={{ style: { top: 52, height: 3 } }}
          style={{ minHeight: 0 }}
        >
          <Tab
            icon={
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img
                  src={AccesorialsIcon}
                  alt="Accesorials"
                  style={{ width: 24, height: 24, marginRight: 4, marginTop: 8, display: "inline-block" }}
                />
              </span>
            }
            label={
              <Typography variant="subtitle2" color="primary" style={{ fontWeight: 700 }}>
                {t("LocationStep.addDeliveryAccessorials")}
              </Typography>
            }
          />
          <Tab
            icon={
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img
                  src={NotesIcon}
                  alt="Add Notes"
                  style={{ width: 24, height: 24, marginRight: 4, marginTop: 8, display: "inline-block" }}
                />
              </span>
            }
            label={
              <Typography variant="subtitle2" color="primary" style={{ fontWeight: 700 }}>
                {t("LocationStep.addNotes")}
              </Typography>
            }
          />
        </Tabs>
        {showDateTime && (
          <div>
            {useTimeRange ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setUseTimeRange(false)}
              >
                {t("LocationStep.useTime")}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setUseTimeRange(true)}
              >
                {t("LocationStep.useTimeRange")}
              </Typography>
            )}
          </div>
        )}
      </div>
      <TabPanel value={tabValue} index={0}>
        {accessorials && accessorials.length > 0 && (
          <Controller
            control={control}
            name={`accessorials`}
            render={({ field }) => (
              <div className="grid grid-cols-2 max-w-[50%]">
                {accessorials.map((accessorial) => {
                  const checked = Array.isArray(field.value)
                    ? field.value.some((a) => Number(a.id) === Number(accessorial.id))
                    : false;
                  return (
                    <div key={accessorial.id} className="flex items-center">
                      <Checkbox
                        checked={checked}
                        onChange={() => {
                          let newValue: ShipmentAccessorial[] = Array.isArray(field.value) ? [...field.value] : [];
                          if (checked) {
                            newValue = newValue.filter((a) => a.id !== Number(accessorial.id));
                          } else {
                            newValue.push({
                              ...accessorial,
                              type:
                                (accessorial.type as unknown as ShipmentAccessorialType) ??
                                ShipmentAccessorialType.Delivery,
                              id: Number(accessorial.id),
                            });
                          }
                          setQuoteRequestByPath({ path: `load_spec.accessorials`, value: newValue });
                        }}
                      />
                      <Typography variant="body2">{accessorial.name}</Typography>
                    </div>
                  );
                })}
              </div>
            )}
          />
        )}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <div className="max-w-[50%]">
          <Controller
            control={control}
            name={`${formKeyPrefix}.note`}
            render={({ field }) => (
              <MemoizedTextInput
                value={field.value}
                onChange={(e) => setQuoteRequestByPath({ path: `stops.${indexByPrefix}.note`, value: e.target.value })}
                margin="normal"
                fullWidth
                multiline
                rows={3}
                placeholder={t("LocationStep.notesPlaceholder")}
              />
            )}
          />
        </div>
      </TabPanel>
    </div>
  );
};
